import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle,
  MessageSquare,
  Users,
  Timer,
  HelpCircle,
  FileText,
  Mic
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TodaysMeet = () => {
  const faqData = [
    {
      question: "What was TodaysMeet?",
      answer: "TodaysMeet was a classroom backchannel chat platform created in 2008 by James Socol. It allowed teachers to create instant chat rooms where students could post short messages during class, giving every student a digital voice in discussions."
    },
    {
      question: "Why did TodaysMeet shut down?",
      answer: "TodaysMeet shut down on June 16, 2018 because its solo creator James Socol couldn't maintain it alongside his full-time job. GDPR compliance requirements and the overhead of running a free service for millions of messages contributed to the decision."
    },
    {
      question: "Is CSW a good TodaysMeet alternative?",
      answer: "Yes! While CSW is broader than just a chat tool, it provides virtual rooms where students can interact via video, chat, and shared focus tools - maintaining the collaborative spirit TodaysMeet championed."
    },
    {
      question: "How did TodaysMeet work?",
      answer: "Teachers created a room in seconds, shared a link, and students joined with just a nickname - no login required. Students could post short messages (originally 140 characters) visible to everyone, creating a live text stream during class."
    },
    {
      question: "What made TodaysMeet special for education?",
      answer: "TodaysMeet gave shy students a voice, allowing them to participate in discussions by typing rather than speaking aloud. It worked on any device with a browser and rooms were ephemeral, addressing privacy concerns."
    },
    {
      question: "What alternatives exist for classroom backchannels?",
      answer: "After TodaysMeet, educators moved to tools like Backchannel Chat, YoTeach, Padlet, or the chat features in Zoom and Teams. CSW offers a different approach with persistent virtual study rooms for collaborative learning."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "TodaysMeet Alternative - Free Classroom Collaboration Platform",
    "description": "Looking for a TodaysMeet alternative? TodaysMeet shut down in 2018. CSW offers free virtual study rooms with chat, video, and focus tools for students.",
    "url": "https://chillstudy.work/todaysmeet/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "TodaysMeet",
      "applicationCategory": "EducationApplication",
      "operatingSystem": "Web"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser"
    },
    "keywords": "TodaysMeet alternative, TodaysMeet replacement, TodaysMeet shut down, classroom backchannel, student voice tool"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1000",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Virtual study rooms",
      "Focus timer",
      "Task management",
      "Video collaboration",
      "Study communities",
      "Reminders",
      "AI-powered flashcards",
      "Progress tracking"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>TodaysMeet Alternative - Free Classroom Collaboration | CSW</title>
        <meta name="description" content="Looking for a TodaysMeet alternative? TodaysMeet shut down in 2018. CSW offers free virtual study rooms with chat, video, and focus tools for students." />
        <meta name="keywords" content="TodaysMeet alternative, TodaysMeet replacement, TodaysMeet shut down, classroom backchannel, student voice, classroom chat" />
        <link rel="canonical" href="https://chillstudy.work/todaysmeet/" />
        <meta property="og:title" content="TodaysMeet Alternative - Free Classroom Collaboration" />
        <meta property="og:description" content="TodaysMeet shut down in 2018. CSW offers free virtual study rooms for collaborative learning." />
        <meta property="og:url" content="https://chillstudy.work/todaysmeet/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertTriangle className="w-4 h-4" />
              TodaysMeet shut down • June 16, 2018
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              TodaysMeet Alternative
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              TodaysMeet gave every student a digital voice for 10 years before shutting down in 2018. 
              Looking for ways to engage students? <Link to="/study-with-me/" className="text-primary hover:underline">CSW</Link> offers 
              free virtual <Link to="/group-study/" className="text-primary hover:underline">study rooms</Link> where everyone can participate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/todaysmeet/history/">
                  Read TodaysMeet History
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-lg bg-teal-500/10 text-teal-500">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">What Happened to TodaysMeet?</h2>
                  <p className="text-muted-foreground">
                    TodaysMeet was created in 2008 by James Socol as a simple backchannel chat for classrooms. 
                    It became the go-to solution for classroom discussions and conference Q&A sessions. 
                    After 10 years of running as a "labor of love," Socol had to shut it down due to lack of 
                    time to maintain it alongside new requirements like GDPR compliance.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">2008</div>
                  <div className="text-sm text-muted-foreground">Created by James Socol</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">10 Years</div>
                  <div className="text-sm text-muted-foreground">Serving educators worldwide</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive mb-1">June 2018</div>
                  <div className="text-sm text-muted-foreground">Shut down permanently</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why CSW Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Why CSW is a Great Alternative</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Users className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Give Everyone a Voice</h3>
                <p className="text-muted-foreground">
                  Like TodaysMeet empowered quiet students, CSW's virtual rooms let everyone participate 
                  through <Link to="/study-with-strangers/" className="text-primary hover:underline">chat, video, or shared activities</Link>.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <MessageSquare className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Real-Time Interaction</h3>
                <p className="text-muted-foreground">
                  Join <Link to="/study-stream/" className="text-primary hover:underline">study streams</Link> and 
                  interact with peers in real-time, just like TodaysMeet's live chat during class.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Timer className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Focus Tools</h3>
                <p className="text-muted-foreground">
                  Go beyond chat with our <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro timer</Link> and 
                  focus modes to help students concentrate during sessions.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Mic className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Video & Audio Support</h3>
                <p className="text-muted-foreground">
                  While TodaysMeet was text-only, CSW offers video and audio for richer <Link to="/study-with-friends/" className="text-primary hover:underline">collaboration</Link> and connection.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <CheckCircle2 className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Free Forever</h3>
                <p className="text-muted-foreground">
                  Like TodaysMeet was free for educators, CSW remains completely free with unlimited 
                  access for students worldwide.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Clock className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Actively Maintained</h3>
                <p className="text-muted-foreground">
                  Unlike TodaysMeet's one-person operation, CSW has a dedicated team ensuring the platform 
                  remains stable and updated.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 border border-border/50">
              <div className="flex items-center gap-3 mb-6">
                <HelpCircle className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Learn More About TodaysMeet</h2>
            <div className="space-y-3">
              <a 
                href="https://www.freetech4teachers.com/2018/05/todaysmeet-is-shutting-down.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Free Tech 4 Teachers: TodaysMeet Is Shutting Down</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://mguhlin.org/2018/05/02/big-changes-for-todaysmeet-and-dotstorming-users/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Miguel Guhlin: Big Changes for TodaysMeet Users</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Give Every Student a Voice?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Continue the spirit of TodaysMeet with CSW's interactive study rooms. 
              Free, easy to use, and designed for collaborative learning.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TodaysMeet;
